/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { PatternInput } from 'src/luis/verticals/patternInput/PatternInput';
import { PatternCellOptions } from 'src/luis/verticals/patternsTable/components/PatternCellOptions';

export type PatternRowProps = {
    /**
     * The pattern to be displayed if exists.
     */
    pattern: Pattern;

    /**
     * The entities list.
     */
    entities: Entity[];

    /**
     * If the displayed pattern is in edit mode or not.
     */
    isInEditMode: boolean;

    /**
     * Callback for updating an existing pattern.
     */
    onUpdatePattern: (pattern: Pattern) => boolean | void;

    /**
     * Callback for deleting an existing pattern.
     */
    onDeletePattern: (pattern: Pattern) => boolean | void;

    /**
     * Callback for creating an entity.
     */
    onCreateNewEntity: (name: string) => boolean | void;

    /**
     * Callback for creating a role.
     */
    onCreateNewRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * Callback for creating an entity and a role.
     */
    onCreateNewEntityAndRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * Callback function fired when the pattern edit mode changed
     */
    onEdit: (val: boolean) => void;

    /**
     * If a pattern can be edited or deleted
     */
    canPatternBeEditedOrDeleted: boolean;
};

const theme = getScopedTheme('PatternsPage');

/**
 * The view of the first cell in the patterns table that contain:
 *
 * 1. The pattern text itself or the editable input field if
 *    the edit mode is enabled.
 *
 * 2. The contextual menu of the specified pattern to support
 *    additional actions to be applied to the selected pattern.
 *
 * @param props The properties of the row which is the pattern
 * to be displayed.
 */
export const PatternRow = (props: PatternRowProps) => {
    const {
        pattern,
        entities,
        onUpdatePattern,
        onDeletePattern,
        onCreateNewEntity,
        onCreateNewEntityAndRole,
        onCreateNewRole,
        isInEditMode,
        onEdit,
        canPatternBeEditedOrDeleted
    } = props;
    const [isHovering, setIsHovering] = React.useState(false);

    /**
     * Submits the pattern with its new text whenever
     * the pattern input submits.
     *
     * @param patternText The new pattern text.
     */
    const onPatternInputSubmit = (patternText: string) => {
        onUpdatePattern({ ...pattern, text: patternText });
        onEdit(false);
    };

    return (
        <Stack
            verticalAlign="center"
            verticalFill
            horizontal
            onFocus={() => setIsHovering(true)}
            onMouseEnter={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
            onMouseLeave={() => setIsHovering(false)}>
            <Stack.Item grow>
                {isInEditMode ? (
                    <PatternInput
                        entities={entities}
                        initialPattern={pattern.text}
                        onPatternSubmit={onPatternInputSubmit}
                        onCreateNewRole={onCreateNewRole}
                        onCreateNewEntity={onCreateNewEntity}
                        onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                        onExitEditMode={() => onEdit(false)}
                    />
                ) : (
                    <LuisText color={theme.textColor} size="16px">
                        {pattern.text}
                    </LuisText>
                )}
            </Stack.Item>

            <PatternCellOptions
                isHidden={!isHovering || isInEditMode || !canPatternBeEditedOrDeleted}
                onClickEdit={() => onEdit(true)}
                onClickDelete={() => onDeletePattern(pattern)}
            />
        </Stack>
    );
};
